import { collabStateManager } from '@databases/collab-state-manager';
import { type CollabManager } from '@databases/collab-state-manager/collab-manager';

import { captureException } from '@canvas/sentry-react';

import { syncDatabasePaywallFeatureState } from './database-paywall-feature-state-cache.ts';
import { type Recyclable } from './types.ts';

const noopPaywallFeatureFetch = () => undefined;

export const initializeRecyclableDatabasePaywallFeatures = (): Recyclable => {
  let unsubscribeFromCollabManagerAttached: (() => void) | undefined;
  let wiredCollabManager: CollabManager | undefined;
  let activeSetupVersion = 0;

  return {
    setup(contentId: string) {
      const setupVersion = ++activeSetupVersion;

      const wireCollabManager = (collabManager: CollabManager) => {
        wiredCollabManager?.tokenManager.onPaywallFeatureFetch(noopPaywallFeatureFetch);
        wiredCollabManager = collabManager;

        const syncIfCurrentCollabManager = (
          featureState: Parameters<typeof syncDatabasePaywallFeatureState>[0],
        ) => {
          if (
            setupVersion !== activeSetupVersion ||
            wiredCollabManager !== collabManager
          ) {
            return;
          }

          syncDatabasePaywallFeatureState(featureState);
        };

        // DB-to-DB transitions reuse the app shell, so the paywall listener needs
        // to follow the newly attached collab manager for each database identity.
        collabManager.tokenManager.onPaywallFeatureFetch(syncIfCurrentCollabManager);

        void collabManager.tokenManager
          .token()
          .then((token) => {
            if (!token.features) {
              return;
            }

            syncIfCurrentCollabManager(token.features);
          })
          .catch((error) => {
            captureException(error, {
              tags: {
                captureLocation: 'initialize-recyclable-database-paywall-features-token',
              },
            });
          });
      };

      unsubscribeFromCollabManagerAttached = collabStateManager.onCollabManagerAttached(
        contentId,
        wireCollabManager,
      );
    },

    dispose() {
      activeSetupVersion++;
      unsubscribeFromCollabManagerAttached?.();
      unsubscribeFromCollabManagerAttached = undefined;
      // onPaywallFeatureFetch is a setter, not additive. Replace the handler
      // with a no-op on dispose so the old collab manager cannot sync stale data.
      wiredCollabManager?.tokenManager.onPaywallFeatureFetch(noopPaywallFeatureFetch);
      wiredCollabManager = undefined;
    },
  };
};
